<template>
  <q-toolbar class="q-py-sm">
    <q-input
      v-if="withSearch"
      :model-value="currentSearch"
      :class="{'col-lg-4 col-md-6': tableMode, 'col-12': !withControlButtons, 'col-6': withControlButtons}"
      :debounce="searchDebounce"
      :autofocus="searchAutofocus"
      type="search"
      placeholder="Search..."
      dense
      outlined
      clearable
      style="max-width: 400px; min-width: 200px;"
      v-bind="searchInputProps"
      @update:model-value="$emit('update:currentSearch', $event)"
    >
      <template #prepend>
        <q-icon name="mdi-magnify" />
      </template>
    </q-input>

    <template v-if="withControlButtons && !!$slots['table-control-buttons']">
      <q-space />
      <slot name="table-control-buttons" />
    </template>
  </q-toolbar>

  <div
    v-if="withDismissibleFilters && hasActiveFilters"
    class="q-px-sm q-pb-sm q-mx-xs"
  >
    <q-card
      flat
      bordered
      class="text-no-wrap overflow-auto"
    >
      <div class="q-px-md q-py-xs">
        <template v-if="!isMobile">
          <div class="row items-center q-gutter-md">
            <div class="col-shrink">
              <span>Filtering By: </span>
            </div>
            <div class="col">
              <template v-for="(filter, key) in currentFilters" :key="key">
                <template v-if="filter.value !== null && Array.isArray(filter.value)">
                  <q-chip
                    v-for="value in filter.value"
                    :key="`${key}-${value}`"
                    removable
                    square
                    color="grey-3"
                    @remove="dismissFilter(key, value)"
                  >
                    <span>{{ filter.label }}:</span>
                    <span class="text-bold q-ml-xs">
                      {{ formatFilterString(filter, value) }}
                    </span>
                  </q-chip>
                </template>

                <template v-else-if="filter.value !== null">
                  <q-chip
                    :key="`${key}-${filter.value}`"
                    removable
                    square
                    color="grey-3"
                    @remove="dismissFilter(key, filter.value)"
                  >
                    <span>{{ filter.label }}:</span>
                    <span class="text-bold q-ml-xs">
                      {{ formatFilterString(filter, filter.value) }}
                    </span>
                  </q-chip>
                </template>
              </template>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="row items-center q-gutter-md">
            <div class="col-shrink">
              <span>Applying <strong>{{ activeFilterCount }}</strong> filters</span>
            </div>
            <div class="col-grow text-right">
              <q-btn
                flat
                round
                dense
                icon="close"
                @click="clearFilters"
              />
            </div>
          </div>
        </template>
      </div>
    </q-card>
  </div>
</template>

<script>
import { Platform } from 'quasar'

export default {
  props: {
    withSearch: {
      type: Boolean,
      default: false
    },
    withDismissibleFilters: {
      type: Boolean,
      default: false
    },
    withControlButtons: {
      type: Boolean,
      default: false
    },
    currentSearch: {
      type: String,
      default: null
    },
    currentFilters: {
      type: Object,
      default: null
    },
    searchDebounce: {
      type: Number,
      default: 750
    },
    searchAutofocus: {
      type: Boolean,
      default: Platform.is.desktop
    },
    tableMode: {
      type: Boolean,
      default: false
    },
    searchInputProps: {
      type: Object
    }
  },
  emits: ['update:currentSearch', 'dismiss', 'clear'],
  computed: {
    isMobile () {
      return this.windowWidth < 700
    },
    hasActiveFilters () {
      if (!this.currentFilters) {
        return false
      }

      return Object.values(this.currentFilters).some(filter => {
        return filter.value !== null && !(Array.isArray(filter.value) && filter.value.length === 0)
      })
    },
    activeFilterCount () {
      let count = 0
      for (const key in this.currentFilters) {
        const filter = this.currentFilters[key]
        if (Array.isArray(filter.value) && filter.value !== null) {
          count += filter.value.length
        }
        else if (filter.value !== null) {
          count += 1
        }
      }
      return count
    }
  },
  methods: {
    dismissFilter (key, value) {
      this.$emit('dismiss', key, value)
    },
    clearFilters () {
      this.$emit('clear')
    },
    formatFilterString (filter, value) {
      let formattedValue = value

      if (filter.isDate) {
        const parsedDate = this.luxon.fromISO(value)

        if (parsedDate.isValid) {
          // Format the date to 'MM/DD/YYYY'.
          formattedValue = this.$formatDate(parsedDate, this.luxonFormats.SHORT_WORDED_DATE)
        }
      }

      return `${filter.prefix || ''}${formattedValue}`
    }
  }
}
</script>
